"use client";

import {
 createContext,
 use,
 useState,
 useMemo,
 useCallback,
 type ReactNode,
} from "react";
import { addDays } from "date-fns";
import { getTruckloadQuantityForType } from "@/lib/generated/product-types";
import type { SpecsRecord } from "@/components/specs/types";

export type SearchMode = "explicit" | "viewport";
export type HeroIntent = "buy" | "sell" | "recycle" | null;

const getDefaultDeliveryDate = () => addDays(new Date(), 1);

function getDefaultQuantityForProductType(productType: string | null): number {
 if (!productType) return DEFAULT_QUANTITY;
 return getTruckloadQuantityForType(productType);
}

interface SearchContextValue {
 intent: HeroIntent;
 setIntent: (intent: HeroIntent) => void;
 productType: string | null;
 setProductType: (type: string | null) => void;
 setProductTypeWithQuantity: (type: string | null) => void;
 quantity: number;
 setQuantity: (count: number) => void;
 deliveryDate: Date | null;
 setDeliveryDate: (date: Date | null) => void;
 specs: SpecsRecord;
 setSpecs: (specs: SpecsRecord) => void;
 selectedSizePreset: string | null;
 setSelectedSizePreset: (preset: string | null) => void;
 condition: string | null;
 setCondition: (condition: string | null) => void;
 budgetCents: number | null;
 setBudgetCents: (cents: number | null) => void;
 askingPriceCents: number | null;
 setAskingPriceCents: (cents: number | null) => void;
 radiusMiles: number;
 setRadiusMiles: (miles: number) => void;
 searchMode: SearchMode;
 setSearchMode: (mode: SearchMode) => void;
 searchTriggered: boolean;
 setSearchTriggered: (triggered: boolean) => void;
 resetSearch: () => void;
}

export const DEFAULT_RADIUS_MILES = 50;
export const DEFAULT_QUANTITY = 1;

const SearchContext = createContext<SearchContextValue | null>(null);

interface SearchProviderProps {
 children: ReactNode;
 initialQuantity?: number;
 initialRadiusMiles?: number;
}

function useProductSearchState(initialQuantity: number | undefined) {
 const [productType, setProductType] = useState<string | null>(null);
 const [quantity, setQuantity] = useState(initialQuantity ?? DEFAULT_QUANTITY);
 const [specs, setSpecs] = useState<SpecsRecord>({});
 const [selectedSizePreset, setSelectedSizePreset] = useState<string | null>(null);
 const [condition, setCondition] = useState<string | null>(null);
 const [budgetCents, setBudgetCents] = useState<number | null>(null);
 const [askingPriceCents, setAskingPriceCents] = useState<number | null>(null);

 const setProductTypeWithQuantity = useCallback((type: string | null) => {
  setProductType(type);
  setQuantity(getDefaultQuantityForProductType(type));
  setSpecs({});
  setSelectedSizePreset(null);
  setCondition(null);
  setBudgetCents(null);
  setAskingPriceCents(null);
 }, []);

 return useMemo(
  () => ({
   productType, setProductType, setProductTypeWithQuantity,
   quantity, setQuantity, specs, setSpecs,
   selectedSizePreset, setSelectedSizePreset, condition, setCondition,
   budgetCents, setBudgetCents, askingPriceCents, setAskingPriceCents,
  }),
  [productType, setProductTypeWithQuantity, quantity, specs, selectedSizePreset, condition, budgetCents, askingPriceCents],
 );
}

function useSearchModeState(initialRadiusMiles: number | undefined) {
 const [intent, setIntent] = useState<HeroIntent>(null);
 const [deliveryDate, setDeliveryDate] = useState<Date | null>(null);
 const [radiusMiles, setRadiusMiles] = useState(initialRadiusMiles ?? DEFAULT_RADIUS_MILES);
 const [searchMode, setSearchMode] = useState<SearchMode>("explicit");
 const [searchTriggered, setSearchTriggered] = useState(false);

 return useMemo(
  () => ({
   intent, setIntent, deliveryDate, setDeliveryDate, radiusMiles, setRadiusMiles,
   searchMode, setSearchMode, searchTriggered, setSearchTriggered,
  }),
  [intent, deliveryDate, radiusMiles, searchMode, searchTriggered],
 );
}

export function SearchProvider({ children, initialQuantity, initialRadiusMiles }: SearchProviderProps) {
 const product = useProductSearchState(initialQuantity);
 const mode = useSearchModeState(initialRadiusMiles);
 const { setProductTypeWithQuantity } = product;
 const { setIntent, setDeliveryDate, setRadiusMiles, setSearchMode, setSearchTriggered } = mode;

 const resetSearch = useCallback(() => {
  setIntent(null);
  setProductTypeWithQuantity(null);
  setDeliveryDate(getDefaultDeliveryDate());
  setRadiusMiles(DEFAULT_RADIUS_MILES);
  setSearchMode("explicit");
  setSearchTriggered(false);
 }, [setIntent, setProductTypeWithQuantity, setDeliveryDate, setRadiusMiles, setSearchMode, setSearchTriggered]);

 const value = useMemo(
  () => ({ ...product, ...mode, resetSearch }),
  [product, mode, resetSearch],
 );

 return (
  <SearchContext value={value}>{children}</SearchContext>
 );
}

const defaultSearchContext: SearchContextValue = {
 intent: null,
 setIntent: () => {},
 productType: null,
 setProductType: () => {},
 setProductTypeWithQuantity: () => {},
 quantity: 1,
 setQuantity: () => {},
 deliveryDate: null,
 setDeliveryDate: () => {},
 specs: {},
 setSpecs: () => {},
 selectedSizePreset: null,
 setSelectedSizePreset: () => {},
 condition: null,
 setCondition: () => {},
 budgetCents: null,
 setBudgetCents: () => {},
 askingPriceCents: null,
 setAskingPriceCents: () => {},
 radiusMiles: DEFAULT_RADIUS_MILES,
 setRadiusMiles: () => {},
 searchMode: "explicit",
 setSearchMode: () => {},
 searchTriggered: false,
 setSearchTriggered: () => {},
 resetSearch: () => {},
};

export function useSearchContext() {
 const context = use(SearchContext);
 return context ?? defaultSearchContext;
}

export {
 SearchContext,
 getDefaultQuantityForProductType,
 getDefaultDeliveryDate,
};
